import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Heart, Play, MapPin, ChevronRight, Cross } from 'lucide-react';

export const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div data-testid="home-page" className="min-h-screen">
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1653133672754-82025e7e9074?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#8B4513]/80 via-[#D2691E]/70 to-[#CD853F]/80" />
        
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '400px',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-32 pb-20">
          <motion.div {...fadeInUp} className="space-y-8 text-center max-w-5xl mx-auto">
            <div className="inline-block">
              <span className="text-sm font-body font-bold tracking-widest uppercase text-amber-200 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-amber-300/30">
                Welcome Home
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-heading font-bold text-white leading-tight drop-shadow-2xl">
              Igniting Faith.<br />
              Empowering Lives.<br />
              <span className="text-amber-300">Transforming Generations.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/95 leading-relaxed max-w-3xl mx-auto drop-shadow-lg">
              We're not just a church—we're a movement. A refuge where broken lives are restored, dormant callings are awakened, and ordinary believers become extraordinary world-changers for Christ.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                data-testid="hero-plan-visit-button"
                className="group bg-white/95 text-primary px-8 py-4 rounded-full font-body font-semibold text-lg hover:bg-white transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 flex items-center justify-center gap-2"
              >
                Plan Your Visit
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/sermons"
                data-testid="hero-watch-services-button"
                className="group border-2 border-white text-white px-8 py-4 rounded-full font-body font-semibold text-lg hover:bg-white hover:text-primary transition-all duration-300 backdrop-blur-sm flex items-center justify-center gap-2"
              >
                <Play className="w-5 h-5" />
                Watch Services
              </Link>
            </div>

            <div className="flex items-center gap-6 pt-6 justify-center">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-12 h-12 rounded-full border-4 border-white bg-secondary flex items-center justify-center text-secondary-foreground font-bold shadow-xl"
                  >
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <div className="text-sm text-white">
                <p className="font-semibold drop-shadow">Join Our Community</p>
                <p className="text-white/90 drop-shadow">Sunday @ 11:00 AM</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section 
        data-testid="weekly-announcements-section"
        className="py-20 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 50%, #CD853F 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '500px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat'
          }}
        />

        <div className="absolute top-10 right-10 text-white/5 transform rotate-12">
          <svg width="150" height="150" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="10" />
          </svg>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
              Weekly Announcements
            </h2>
            <div className="w-24 h-1 bg-white mx-auto"></div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/95 backdrop-blur-sm rounded-3xl overflow-hidden shadow-2xl"
          >
            <img
              src="/flyers/weekly-announcements.png"
              alt="CRCC Weekly Announcements"
              className="w-full h-auto"
            />
          </motion.div>
        </div>
      </section>

      <section 
        data-testid="features-section" 
        className="py-24 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #DEB887 0%, #D2B48C 50%, #BC8F8F 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '600px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat'
          }}
        />
        
        <div className="absolute top-10 right-10 text-[#8B4513]/5 transform rotate-12">
          <svg width="200" height="200" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="8" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-body font-medium tracking-widest uppercase text-primary">What We Do</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mt-4">
              Our Mission & Vision
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="feature-card-reaching"
              className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-8 border-l-4 border-primary hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-amber-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-foreground mb-4">Reaching Souls</h3>
              <p className="text-muted-foreground leading-relaxed">
                Every soul matters. We're on a mission to reach the lost with the life-changing message of Jesus Christ, bringing hope to the hopeless and light to the darkness.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-testid="feature-card-helping"
              className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-8 border-l-4 border-secondary hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-secondary to-green-700 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-foreground mb-4">Helping the Needy</h3>
              <p className="text-muted-foreground leading-relaxed">
                Compassion in action. We serve our community with genuine love, meeting both physical and spiritual needs—because faith without works is dead.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              data-testid="feature-card-activating"
              className="group relative bg-white/90 backdrop-blur-sm rounded-3xl p-8 border-l-4 border-accent hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-accent to-yellow-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Play className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-foreground mb-4">Activating Leaders</h3>
              <p className="text-muted-foreground leading-relaxed">
                Raising up God's generals. We're equipping and activating the next generation of leaders who will carry the fire of God's presence to the ends of the earth.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section 
        data-testid="cta-section" 
        className="py-24 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8B7355 0%, #A0826D 50%, #8B7355 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '800px',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />

        <div className="absolute bottom-10 left-10 text-[#FAEBD7]/5 transform -rotate-12">
          <svg width="150" height="150" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="10" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1768776184929-614344eecfad?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Community"
                className="rounded-3xl shadow-2xl w-full h-auto border-4 border-white/20"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <span className="text-sm font-body font-medium tracking-widest uppercase text-amber-300">Join Us</span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white">
                Find Your Refuge
              </h2>
              <p className="text-lg text-white/95 leading-relaxed">
                You don't need to be perfect to belong here. Christ Refuge is where the broken find healing, the weary find rest, and the searching find their purpose. This is more than a church—this is family.
              </p>
              <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <MapPin className="w-6 h-6 text-amber-300 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-heading font-semibold text-white mb-1">Visit Us</h4>
                  <p className="text-white/90 text-sm">
                    5700 East 191st Street, Belton, MO 64012
                  </p>
                  <p className="text-sm text-amber-300 font-medium mt-2">Sunday Worship @ 11:00 AM</p>
                </div>
              </div>
              <Link
                to="/about"
                data-testid="learn-more-button"
                className="inline-flex items-center gap-2 bg-white/95 text-primary px-8 py-4 rounded-full font-body font-semibold hover:bg-white transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
              >
                Learn More About Us
                <ChevronRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section 
        data-testid="events-preview-section" 
        className="py-24 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #CD853F 0%, #DAA520 50%, #B8860B 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '500px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat'
          }}
        />

        <div className="absolute top-20 right-20 text-white/5 transform rotate-45">
          <svg width="180" height="180" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="8" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-sm font-body font-medium tracking-widest uppercase text-white/90">Upcoming</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mt-4 mb-6">
              Join Us for These Events
            </h2>
            <Link
              to="/events"
              data-testid="view-all-events-button"
              className="inline-flex items-center gap-2 text-white hover:gap-4 transition-all duration-300 font-semibold text-lg bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/30 hover:bg-white/20"
            >
              View All Events
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
